import React from 'react';
import { CardContainer } from './CardContainer';
import { SectionHeader } from './SectionHeader';
import { Button } from '../design-system/Button';
import { colors, spacing } from '../design-system/tokens';

// Quick Action icon SVG (matching design system style)
const QuickActionIcon: React.FC = () => (
  <svg 
    width="21" 
    height="24" 
    viewBox="0 0 21 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M12.25 2L10.25 7H4.25L8.75 10.5L6.75 16L12.25 12.5L17.75 16L15.75 10.5L20.25 7H14.25L12.25 2Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type ButtonVariant = 'positive' | 'tertiary' | 'neutral' | 'disabled';

export interface QuickActionButton {
  id: string;
  text: string;
  variant: ButtonVariant;
  disabled?: boolean;
  onClick?: () => void;
}

interface QuickActionsCardProps {
  title?: string;
  buttons: QuickActionButton[];
  onButtonClick?: (buttonId: string) => void;
  className?: string;
}

// Map Quick Actions variants to Design System variants
const mapToDesignSystemVariant = (variant: ButtonVariant): 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning' => {
  switch (variant) {
    case 'positive':
      return 'primary'; // Green button
    case 'tertiary':
      return 'secondary'; // Blue button  
    case 'neutral':
      return 'outline'; // Gray button
    case 'disabled':
      return 'ghost'; // Will be disabled
    default:
      return 'outline';
  }
};

export const QuickActionsCard: React.FC<QuickActionsCardProps> = ({ 
  title = "Quick Actions",
  buttons,
  onButtonClick,
  className = '' 
}) => {
  const handleButtonClick = (button: QuickActionButton) => {
    if (!button.disabled) {
      button.onClick?.();
      onButtonClick?.(button.id);
    }
  };

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Header */}
        <SectionHeader 
          title={title}
          icon={<div style={{ width: '21px', height: '24px' }}><QuickActionIcon /></div>}
          showBottomBorder={true}
        />

        {/* Button Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
          gap: spacing.md,
          height: '150px',
          width: '100%'
        }}>
          {buttons.map((button) => {
            // Don't render empty disabled buttons
            if (button.disabled && !button.text.trim()) {
              return <div key={button.id} />; // Empty grid cell
            }
            
            return (
              <Button
                key={button.id}
                variant={mapToDesignSystemVariant(button.variant)}
                size="large"
                fullWidth={true}
                disabled={button.disabled}
                onClick={() => handleButtonClick(button)}
                style={{
                  height: '41px', // Match Figma height
                  fontSize: '20px', // Match Figma button text size
                  fontWeight: 700
                }}
              >
                {button.text}
              </Button>
            );
          })}
        </div>
      </CardContainer>
    </div>
  );
};

export default QuickActionsCard;